import React, { useState, useRef, useEffect, useContext } from "react";
import { useTheme } from "@material-ui/core/styles";
import { format } from "date-fns";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import NotificationsIcon from "@material-ui/icons/Notifications";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { Box, Typography, Tooltip } from "@material-ui/core";
import api from "../../services/api";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 360,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 340,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 290,
		},
	},
	customBadge: {
		backgroundColor: "#f44336",
		color: "white",
		top: 2,
		right: 1,
		border: `1px solid ${theme.palette.background.paper}`,
		height: 18,
		minWidth: 18,
		padding: '0 4px',
		fontSize: '0.7rem'
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(1, 1.5),
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
	},
	compactItem: {
		padding: theme.spacing(1, 1.25),
		alignItems: "flex-start",
	},
	compactAvatar: {
		width: 36,
		height: 36,
        backgroundColor: theme.palette.primary.main,
	},
	compactTopRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
	},
	compactName: {
		fontWeight: 600,
		fontSize: "0.9rem",
		lineHeight: 1.2,
		color: theme.palette.text.primary,
	},
	compactTime: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap",
	},
	compactMessage: {
		fontSize: "0.85rem",
		color: theme.palette.text.secondary,
		marginTop: 2,
		display: "-webkit-box",
		WebkitLineClamp: 3,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		wordBreak: "break-word",
	},
	unreadIndicator: {
		display: "inline-block",
		width: 8,
		height: 8,
		borderRadius: "50%",
		backgroundColor: theme.palette.success.main,
		marginLeft: 8,
	}
}));

const SystemNotificationsPopover = () => {
	const classes = useStyles();
	const theme = useTheme();

	const { user, socket } = useContext(AuthContext);

	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	useEffect(() => {
		const fetchNotifications = async () => {
			try {
				const { data } = await api.get("/system-notifications");
				setNotifications(data);
			} catch (err) {
				toastError(err);
			}
		}

		fetchNotifications();
	}, []);

	useEffect(() => {
		const companyId = user.companyId;
		if (user.id && socket) {
			const onCompanySystemNotification = (data) => {
				if (data.action === "create") {
					setNotifications(prevState => [data.record, ...prevState]);
				}
				if (data.action === "update") {
					setNotifications(prevState => {
						const index = prevState.findIndex(n => n.id === data.record.id);
						if (index !== -1) {
							prevState[index] = data.record;
							return [...prevState];
						}
						return prevState;
					});
				}
			};

			socket.on(`company-${companyId}-system-notification`, onCompanySystemNotification);

			return () => {
				socket.off(`company-${companyId}-system-notification`, onCompanySystemNotification);
			};
		}
		return undefined;
	}, [user, socket]);

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const handleReadNotification = async (notification) => {
		if (notification.isRead) return;

		try {
			await api.put(`/system-notifications/${notification.id}/read`);
			setNotifications(prevState => {
				const index = prevState.findIndex(n => n.id === notification.id);
				if (index !== -1) {
					prevState[index].isRead = true;
					return [...prevState];
				}
				return prevState;
			});
		} catch (err) {
			toastError(err);
		}
	};

	const handleMarkAllAsRead = async () => {
		try {
			await api.put("/system-notifications/actions/read-all");
			setNotifications(prevState => prevState.map(n => ({ ...n, isRead: true })));
		} catch (err) {
			toastError(err);
		}
	};

	const handleClearAll = async () => {
		try {
			await api.delete("/system-notifications/actions/clear");
			setNotifications([]);
		} catch (err) {
			toastError(err);
		}
	};

	const formatNotificationTime = (dateValue) => {
		try {
			return format(new Date(dateValue), "dd/MM HH:mm");
		} catch (_) {
			return "";
		}
	};

	const unreadCount = notifications.filter(n => !n.isRead).length;

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Notificaciones del Sistema"
				color="inherit"
				style={{ color: "white" }}
			>
				<Badge overlap="rectangular" badgeContent={unreadCount} classes={{ badge: classes.customBadge }}>
					<NotificationsIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<Box className={classes.header}>
					<Typography variant="subtitle1" style={{ fontWeight: 600 }}>
						Notificaciones del Sistema
					</Typography>
					<Box>
						<Tooltip title="Marcar todo como leído">
							<IconButton size="small" onClick={handleMarkAllAsRead}>
								<DoneAllIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Limpiar Todo">
							<IconButton size="small" onClick={handleClearAll} style={{ marginLeft: 4 }}>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>No hay notificaciones</ListItemText>
						</ListItem>
					) : (
						notifications.map((notification, index) => (
							<React.Fragment key={notification.id}>
								<ListItem
									button
									className={classes.compactItem}
									onClick={() => handleReadNotification(notification)}
								>
									<ListItemAvatar>
										<Avatar className={classes.compactAvatar}>
											<NotificationsIcon />
										</Avatar>
									</ListItemAvatar>
									<div style={{ width: "100%", minWidth: 0 }}>
										<div className={classes.compactTopRow}>
											<Typography className={classes.compactName} noWrap>
												{notification.title}
												{!notification.isRead && (
													<span className={classes.unreadIndicator}></span>
												)}
											</Typography>
											<Typography className={classes.compactTime}>
												{formatNotificationTime(notification.createdAt)}
											</Typography>
										</div>
										<Typography className={classes.compactMessage}>
											{notification.message}
										</Typography>
									</div>
								</ListItem>
								{index < notifications.length - 1 && <Divider component="li" />}
							</React.Fragment>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default SystemNotificationsPopover;
